<script setup lang="ts">
import { LucideLink } from 'lucide-vue-next'
import { toast } from '~/components/ui/toast/use-toast'
import type { BankingBlockKey } from './types'
import { useBankingNumber } from './useBankingNumber'

const props = defineProps<{
  id: BankingBlockKey
  titleKey: string
  titleParams?: Record<string, string | number>
  asOf: string | null | undefined
}>()

const { t } = useI18n()
const { formatDate } = useBankingNumber()

const headingId = computed(() => `${props.id}-title`)

const copyLink = async () => {
  const url = new URL(window.location.href)

  url.hash = props.id

  try {
    await navigator.clipboard.writeText(url.toString())
    toast({ title: t('banking_statistics.link_copied') })
  } catch {
    // Clipboard access can be denied (insecure context, permissions); the anchor still works.
    window.location.hash = props.id
  }
}
</script>

<template>
  <section
    v-if="asOf"
    :id="id"
    :aria-labelledby="headingId"
    class="scroll-mt-24 rounded-lg border border-gray-200 bg-white p-4 sm:p-5"
  >
    <div class="mb-4 flex items-start justify-between gap-3">
      <h2 :id="headingId" class="text-lg font-semibold leading-snug">
        {{ t(titleKey, titleParams ?? {}) }}
      </h2>
      <Button
        variant="ghost"
        size="sm"
        class="shrink-0 text-gray-500"
        :title="t('banking_statistics.copy_link')"
        :aria-label="t('banking_statistics.copy_link')"
        @click="copyLink"
      >
        <LucideLink class="size-4" />
        <span class="hidden sm:inline">{{ t('banking_statistics.copy_link') }}</span>
      </Button>
    </div>

    <slot />

    <div class="text-muted-foreground mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t pt-3 text-xs">
      <span>{{ t('banking_statistics.source_as_of', { date: formatDate(asOf) }) }}</span>
      <slot name="footer" />
    </div>
  </section>
</template>
